import React, { createContext, useState, useContext, useEffect } from 'react';

import * as authApi from '@api/auth';
const AUTH_SESSION_KEY = 'authSession';
const AUTH_SESSION_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const restoreSession = async () => {
      let restoredToken = null;
      const savedSession = localStorage.getItem(AUTH_SESSION_KEY);
      if (savedSession) {
        try {
          const session = JSON.parse(savedSession);
          if (session.expiresAt > Date.now()) {
            login(session.token, session.email, session.isAdmin);
            restoredToken = session.token;
          } else {
            localStorage.removeItem(AUTH_SESSION_KEY);
          }
        } catch {
          localStorage.removeItem(AUTH_SESSION_KEY);
        }
      }

      try {
        const data = await authApi.refreshSession();
        login(data.token, data.email);
        restoredToken = data.token;
      } catch {
        // no refresh cookie / expired — leave user logged out
      }

      try {
        if (restoredToken) {
          const account = await authApi.fetchCurrentUser(restoredToken);
          updateUser({
            email: account.email,
            isAdmin: !!account.is_admin,
          });
        }
      } catch {
        // token is valid but /me failed — keep the user logged in
      } finally {
        setAuthReady(true);
      }
    };
    restoreSession();
  }, []);

  const login = (token, email, isAdmin = false) => {
    const user = { email, isAdmin };
    setToken(token);
    setUser(user);
    localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify({
      token,
      email,
      isAdmin,
      expiresAt: Date.now() + AUTH_SESSION_DURATION_MS,
    }));
  };

  const logout = () => {
    authApi.logout().catch(() => {});
    setToken(null);
    setUser(null);
    localStorage.removeItem(AUTH_SESSION_KEY);
  };

  const updateUser = (newUser) => {
    setUser(newUser);
    const savedSession = localStorage.getItem(AUTH_SESSION_KEY);
    if (savedSession) {
      try {
        localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify({
          ...JSON.parse(savedSession),
          ...newUser,
        }));
      } catch {
        localStorage.removeItem(AUTH_SESSION_KEY);
      }
    }
  };

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ token, user, login, logout, isAuthenticated, authReady, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);